<template>
  <button
    ref="$el"
    :class="cmpClass"
    :disabled="disabled"
    @click="e => handleClick(e)"
    @mousedown="e => handleMousedown(e)"
    @mouseenter="e => handleMouseenter(e)"
    @mouseleave="e => handleMouseleave(e)"
    @mouseup="e => handleMouseup(e)"
  >
    <div class="d-button-content">
      <template
        v-if="(iconPosition === 'left' && (loading || icon)) || (!iconPosition && icon)"
      >
        <Icon
          v-if="loading"
          class="icon-left"
          :style="iconSpace && { marginRight: iconSpace }"
          :icon="LoadingFour"
          color="primary"
          spin
        />
        <Icon
          v-else
          class="icon-left"
          :style="iconSpace && { marginRight: iconSpace }"
          v-bind="iconProps"
        />
      </template>
      <Text
        v-if="$slots.default"
        class="d-text-nowrap"
        color="current"
        ellipsis
        tooltip
      >
        <slot />
      </Text>
      <template
        v-if="(iconPosition === 'right' && (loading || icon)) || (!iconPosition && !icon && loading)"
      >
        <Icon
          v-if="loading"
          class="icon-right"
          :style="iconSpace && { marginLeft: iconSpace }"
          :icon="LoadingFour"
          color="primary"
          spin
        />
        <Icon
          v-else
          class="icon-right"
          :style="iconSpace && { marginLeft: iconSpace }"
          v-bind="iconProps"
        />
      </template>
    </div>
  </button>
</template>

<script setup lang="ts">
  import {
    ref, reactive, watch, watchEffect, computed, useSlots, withDefaults,
  } from 'vue'
  import { Text } from '../Text'
  import { Icon, IconProps } from '../Icon'
  import { LoadingFour } from '../../icons'
  import { IconContent } from '../Icon/interface'
  import { ButtonType, ButtonSize, ButtonIconPosition } from '../../enums'
  import { MouseState } from '../../states'

  const props = withDefaults(
    defineProps<{
      type?: ButtonType
      size?: ButtonSize
      icon?:((p: IconProps) => string) | Partial<IconContent>
      iconPosition?: ButtonIconPosition
      iconSpace?: string | number
      disabled?: boolean
      loading?: boolean
      round?: boolean
      block?: boolean
    }>(),
    {
      type: 'default',
      size: 'default',
      icon: undefined,
      iconPosition: undefined,
      iconSpace: undefined,
      disabled: false,
      loading: false,
      round: false,
      block: false,
    },
  )

  const emits = defineEmits<{(e: 'click', event: MouseEvent): void; (e: 'mousedown', event: MouseEvent): void; (e: 'mouseenter', event: MouseEvent): void; (e: 'mouseleave', event: MouseEvent): void; (e: 'mouseup', event: MouseEvent): void; (e: 'state-change', state: MouseState): void}>()

  const slots = useSlots()

  const state = reactive<MouseState>({
    pressing: false,
    hover: false,
  })

  watch(
    state,
    v => emits('state-change', v),
  )

  const iconProps = computed(
    () => ({
      size: props.size,
      ...typeof props.icon === 'function'
        ? { icon: props.icon }
        : props.icon,
    }),
  )

  const iconSpace = computed(
    () => props.iconSpace && (
      typeof props.iconSpace === 'number'
        ? `${props.iconSpace}px`
        : props.iconSpace
    ),
  )

  const disabled = computed(
    () => props.disabled || props.loading,
  )

  const cmpClass = computed(
    () => {
      const general = [
        'd-button',
        props.round && 'd-button-round',
        props.block && 'd-button-block',
        `d-button-${props.size}`,
        ...props.size === 'large'
          ? [
            '--size-icon-large',
            '--size-text-h6',
          ]
          : [],
        disabled.value && 'disabled',
        slots.default && 'd-button-with-content',
        state.pressing && 'pressing',
        '--color-static',
        'bold',
      ]

      switch (props.type) {
        case 'primary':
          return [
            ...general,
            '--color-bg-primary',
            '--color-white',
          ]
        case 'secondary':
          return [
            ...general,
            '--color-bg-fill',
            '--color-primary',
          ]
        case 'danger':
          return [
            ...general,
            '--color-bg-fill',
            '--color-danger',
          ]
        case 'light':
          return [
            ...general,
            '--color-bg-fill-light',
            disabled.value
              ? '--color-text-disabled'
              : '--color-text-paragraph',
          ]
        default:
          return [
            ...general,
            '--color-bg-fill',
            disabled.value
              ? '--color-text-disabled'
              : '--color-text-paragraph',
          ]
      }
    },
  )

  function handleClick(e: MouseEvent) {
    emits('click', e)
  }

  function handleMousedown(e: MouseEvent) {
    emits('mousedown', e)
    state.pressing = true
  }

  function handleMouseenter(e: MouseEvent) {
    emits('mouseenter', e)
    state.hover = true
  }

  function handleMouseleave(e: MouseEvent) {
    emits('mouseleave', e)
    state.pressing = false
    state.hover = false
  }

  function handleMouseup(e: MouseEvent) {
    emits('mouseup', e)
    state.pressing = false
  }

  const $el = ref<HTMLElement>()

  const exposed = reactive({
    $el: $el.value,
  })

  watchEffect(
    () => {
      exposed.$el = $el.value
    },
  )

  defineExpose(exposed)
</script>
